// The review model (docs/design.md "Review marks"): from a comparison's
// changes and the reviewer's marks, per change, file and pull request, the
// lines reviewed and the status. Every view of "is this reviewed" (the
// change's Reviewed checkbox, n/p to the next unreviewed change, the file
// tree's icons and counts, the header's meters, the per-file status cache)
// reads it, so they cannot disagree.
//
// Coverage is an intersection: a changed line is covered when a mark on
// its side's path and blob contains it, or when a mark on an earlier blob
// of the file contains a line that the earlier blob's line map
// (`EngineApi.lineMap`) takes to it. No layout enters: the same marks give
// the same covered lines under every diff algorithm, comparison, fold and
// filter, and only the set of changed lines differs between them.
import type { Anchor, Block, BlockSet, FileSides, Row, Side } from '../engine/types';
import { anchorId, planMark, planUnmark, type Line, type Plan } from './anchors';

/** How much of something (a change, a file, the pull request) is reviewed:
 *  `empty` when it has no changed line, `unreviewed` when none is covered,
 *  `reviewed` when all are, `partial` otherwise; `unknown` for a file whose
 *  changes are not computed yet. */
export type Status = 'empty' | 'unreviewed' | 'partial' | 'reviewed' | 'unknown';

export interface Progress {
  /** Changed lines (removed and added). */
  total: number;
  /** Of those, the covered ones. */
  done: number;
  status: Status;
}

/** A change run as the model reads it: its changed lines on each side. */
export interface ReviewChange {
  /** The change's block and run in its layout (`Row.run`), so a view can
   *  find it; they say nothing about which lines it covers. */
  block: number;
  run: number;
  /** Removed lines (old side), ascending. */
  old: number[];
  /** Added lines (new side), ascending. */
  new: number[];
}

/** One side of a file: its path and blob, and earlier names of the file
 *  whose marks apply to it (a rename the comparison records). */
export interface ReviewSide {
  path: string;
  blob: string;
  renamedFrom?: string[];
}

/** A file of the comparison. */
export interface ReviewFile {
  path: string;
  old: ReviewSide | null;
  new: ReviewSide | null;
  /** Its changes in the layout shown, or null until they are computed. */
  changes: ReviewChange[] | null;
  /** Until `changes` are computed: what a cache last knew for the same
   *  inputs, else changed lines as GitHub counts them, none done. */
  known?: Progress;
  estimate?: number;
}

/** The comparison: its files, and the line maps computed so far, by
 *  `lineMapKey(from blob, to blob)`. */
export interface ReviewDiff {
  files: ReviewFile[];
  lineMaps: ReadonlyMap<string, readonly number[]>;
}

export interface ChangeReview extends Progress {
  block: number;
  run: number;
}

export interface FileReview extends Progress {
  path: string;
  changes: ChangeReview[];
  /** Whether a mark covers line `line` of side `side`. */
  covers(side: Side, line: number): boolean;
  /** The marks that apply to this file and are on another blob than the
   *  side's with no line map yet: what to compute (`carryNeeds`). */
  waiting: { side: Side; from: string; to: string }[];
}

export interface ReviewModel extends Progress {
  /** In the order of `diff.files`. */
  files: FileReview[];
  byPath: ReadonlyMap<string, FileReview>;
  /** Files whose status is `reviewed` or `empty`, of files known. */
  filesReviewed: number;
}

/** The key of the line map from blob `from` to blob `to`. */
export function lineMapKey(from: string, to: string): string {
  return `${from}>${to}`;
}

/** The status of `done` of `total` lines. */
export function statusOf(total: number, done: number): Status {
  if (total === 0) return 'empty';
  if (done === 0) return 'unreviewed';
  return done >= total ? 'reviewed' : 'partial';
}

/**
 * Whether something with changed lines (a change, a fold's lines, a file,
 * the pull request) is unreviewed: at least one of its changed lines in
 * the comparison shown has no mark covering it. This is the one reading
 * of "unreviewed": n / p stop on a change it is true of, `r` marks one it
 * is true of and refuses one it is false of, and the change's and the
 * file's Reviewed checkboxes, the file tree and the meters are ticked or
 * full exactly when it is false. Something with no changed line is not
 * unreviewed: there is nothing in it to mark.
 */
export function isUnreviewed(p: Pick<Progress, 'total' | 'done'>): boolean {
  return p.done < p.total;
}

/**
 * A row's changed line as a mark names it: a removed row's on the old
 * side, an added row's on the new; null for a row that changes nothing.
 * The changed lines of a comparison are exactly these, wherever they are
 * counted.
 */
export function changedLine(r: Pick<Row, 'kind' | 'old' | 'new'>): Line | null {
  if (r.kind === 'del' && r.old !== null) return { side: 'old', line: r.old };
  if (r.kind === 'add' && r.new !== null) return { side: 'new', line: r.new };
  return null;
}

/** A file's change runs in a layout (`BlockSet.blocks`), in file order. */
export function changesOf(blocks: Block[]): ReviewChange[] {
  const out: ReviewChange[] = [];
  blocks.forEach((b, block) => {
    if (b.kind !== 'change') return;
    const runs = new Map<number, ReviewChange>();
    for (const r of b.rows) {
      const l = changedLine(r);
      if (!l) continue;
      // A changed row always has a run; one without is counted with the block's first.
      const run = r.run ?? 0;
      let c = runs.get(run);
      if (!c) runs.set(run, (c = { block, run, old: [], new: [] }));
      c[l.side].push(l.line);
    }
    out.push(...[...runs.values()].sort((x, y) => x.run - y.run));
  });
  return out;
}

/** A file for the model from its computed diff. */
export function reviewFile(path: string, sides: FileSides, set: BlockSet, renamedFrom: string[] = []): ReviewFile {
  const side = (s: FileSides['old']): ReviewSide | null => (s ? { ...s, renamedFrom } : null);
  return { path, old: side(sides.old), new: side(sides.new), changes: changesOf(set.blocks) };
}

/** Marks indexed by side and path. */
class Index {
  private by = new Map<string, Anchor[]>();
  constructor(marks: readonly Anchor[]) {
    for (const m of marks) {
      const k = `${m.side}\0${m.path}`;
      const list = this.by.get(k);
      if (list) list.push(m);
      else this.by.set(k, [m]);
    }
  }
  /** The marks that can apply to a side: on its path or a former one. */
  of(side: Side, at: ReviewSide): Anchor[] {
    const paths = [at.path, ...(at.renamedFrom ?? [])];
    return [...new Set(paths)].flatMap((p) => this.by.get(`${side}\0${p}`) ?? []);
  }
}

/**
 * Where the lines of `mark` are in the side `at` of a file: each of its
 * lines' number there (0 for none), or null when the mark does not apply
 * (another file, or an earlier blob whose line map is not known).
 */
function image(mark: Anchor, at: ReviewSide, maps: ReviewDiff['lineMaps']): readonly number[] | null {
  const n = mark.endLine - mark.startLine + 1;
  if (mark.blob === at.blob) return Array.from({ length: n }, (_, k) => mark.startLine + k);
  const map = maps.get(lineMapKey(mark.blob, at.blob));
  if (!map) return null;
  return Array.from({ length: n }, (_, k) => map[mark.startLine - 1 + k] ?? 0);
}

function reviewOne(file: ReviewFile, index: Index, maps: ReviewDiff['lineMaps']): FileReview {
  const covered = { old: new Set<number>(), new: new Set<number>() };
  const waiting: FileReview['waiting'] = [];
  for (const side of ['old', 'new'] as const) {
    const at = file[side];
    if (!at) continue;
    for (const m of index.of(side, at)) {
      const lines = image(m, at, maps);
      if (lines) for (const l of lines) l && covered[side].add(l);
      else if (!waiting.some((w) => w.side === side && w.from === m.blob)) waiting.push({ side, from: m.blob, to: at.blob });
    }
  }
  const covers = (side: Side, line: number) => covered[side].has(line);
  if (!file.changes) {
    const known = file.known ?? { total: file.estimate ?? 0, done: 0, status: 'unknown' as const };
    return { path: file.path, ...known, changes: [], covers, waiting };
  }
  let total = 0;
  let done = 0;
  const changes = file.changes.map((c): ChangeReview => {
    const t = c.old.length + c.new.length;
    const d = c.old.filter((l) => covered.old.has(l)).length + c.new.filter((l) => covered.new.has(l)).length;
    total += t;
    done += d;
    return { block: c.block, run: c.run, total: t, done: d, status: statusOf(t, d) };
  });
  return { path: file.path, total, done, status: statusOf(total, done), changes, covers, waiting };
}

/**
 * The review state of a comparison under `marks` (the reviewer's reviewed
 * marks, stored and pending alike). Pure: equal inputs give equal results,
 * and a file's result depends only on that file, the marks on its paths
 * and their line maps.
 */
export function reviewModel(diff: ReviewDiff, marks: readonly Anchor[]): ReviewModel {
  const index = new Index(marks);
  return summarize(diff.files.map((f) => reviewOne(f, index, diff.lineMaps)));
}

/** The anchors that can apply to a file: on either side, at its path or a
 *  former one, on any blob. */
export function marksOfFile(file: Pick<ReviewFile, 'old' | 'new'>, marks: readonly Anchor[]): Anchor[] {
  const paths = new Set([file.old, file.new].flatMap((s) => (s ? [s.path, ...(s.renamedFrom ?? [])] : [])));
  return marks.filter((m) => paths.has(m.path));
}

/**
 * `reviewModel`, remembering each file's result: a file is reviewed again
 * only when its `ReviewFile` object, the marks on its paths or (for a file
 * waiting for one) the line maps changed. So a mark, or one file's diff
 * arriving, costs that file and a sum over the rest, not every file again.
 */
export class ReviewCache {
  private kept = new Map<string, { file: ReviewFile; marks: readonly Anchor[]; maps: ReviewDiff['lineMaps']; out: FileReview }>();
  private ids = new WeakMap<Anchor, string>();
  /** For the marks last given: their index, each path's anchor IDs (joined), and the paths whose marks differ from the marks before. */
  private indexed: { marks: readonly Anchor[]; index: Index; byPath: Map<string, string>; changed: Set<string> } | null = null;

  private id(a: Anchor): string {
    let id = this.ids.get(a);
    if (id === undefined) this.ids.set(a, (id = anchorId(a)));
    return id;
  }

  private index(marks: readonly Anchor[]) {
    if (this.indexed?.marks === marks) return this.indexed;
    const lists = new Map<string, string[]>();
    for (const m of marks) {
      const list = lists.get(m.path);
      if (list) list.push(this.id(m));
      else lists.set(m.path, [this.id(m)]);
    }
    const byPath = new Map([...lists].map(([p, ids]) => [p, ids.sort().join(',')]));
    const before = this.indexed?.byPath ?? new Map<string, string>();
    const changed = new Set<string>();
    for (const [p, ids] of byPath) if (before.get(p) !== ids) changed.add(p);
    for (const p of before.keys()) if (!byPath.has(p)) changed.add(p);
    this.indexed = { marks, index: new Index(marks), byPath, changed };
    return this.indexed;
  }

  review(diff: ReviewDiff, marks: readonly Anchor[]): ReviewModel {
    const before = this.indexed?.marks;
    const { index, changed } = this.index(marks);
    const touched = (f: ReviewFile) =>
      [f.old, f.new].some((s) => !!s && (changed.has(s.path) || (s.renamedFrom ?? []).some((p) => changed.has(p))));
    const seen = new Set<string>();
    const files = diff.files.map((f) => {
      seen.add(f.path);
      const k = this.kept.get(f.path);
      const mapsOk = !!k && (k.maps === diff.lineMaps || !k.out.waiting.length);
      // The same file, reviewed under these marks, or under the marks just
      // before them when none of its paths' marks changed.
      if (k && k.file === f && mapsOk && (k.marks === marks || (k.marks === before && !touched(f)))) {
        k.marks = marks;
        return k.out;
      }
      const out = reviewOne(f, index, diff.lineMaps);
      this.kept.set(f.path, { file: f, marks, maps: diff.lineMaps, out });
      return out;
    });
    for (const p of this.kept.keys()) if (!seen.has(p)) this.kept.delete(p);
    return summarize(files);
  }
}

function summarize(files: FileReview[]): ReviewModel {
  let total = 0;
  let done = 0;
  let filesReviewed = 0;
  let unknown = false;
  for (const f of files) {
    total += f.total;
    done += f.done;
    if (f.status !== 'unknown' && !isUnreviewed(f)) filesReviewed++;
    if (f.status === 'unknown') unknown = true;
  }
  const status = unknown && done < total ? 'unknown' : statusOf(total, done);
  return { files, byPath: new Map(files.map((f) => [f.path, f])), total, done, status, filesReviewed };
}

/** The line maps the model is waiting for, each once: compute
 *  `lineMap(text of from, text of to)` and add it to `diff.lineMaps`. */
export function carryNeeds(model: ReviewModel): { from: string; to: string }[] {
  const seen = new Set<string>();
  const out: { from: string; to: string }[] = [];
  for (const f of model.files)
    for (const w of f.waiting) {
      const k = lineMapKey(w.from, w.to);
      if (!seen.has(k)) {
        seen.add(k);
        out.push({ from: w.from, to: w.to });
      }
    }
  return out;
}

/** The writes that set `lines` of `file` to `value` (mark or unmark),
 *  given the stored `marks` and the comparison's line maps. */
export function planLines(diff: ReviewDiff, file: ReviewFile, marks: readonly Anchor[], lines: Iterable<Line>, value: boolean): Plan {
  const sides: FileSides = {
    old: file.old && { path: file.old.path, blob: file.old.blob },
    new: file.new && { path: file.new.path, blob: file.new.blob },
  };
  if (value) return planMark(sides, marks, lines);
  const applicable = new Index(marks);
  const mine = new Set<Anchor>([
    ...(file.old ? applicable.of('old', file.old) : []),
    ...(file.new ? applicable.of('new', file.new) : []),
  ]);
  return planUnmark(sides, [...mine], lines, (m, side) => image(m, file[side]!, diff.lineMaps));
}

/** The lines of a change: what its Reviewed checkbox marks or unmarks. */
export function changeLines(c: ReviewChange): Line[] {
  return [...c.old.map((line) => ({ side: 'old' as const, line })), ...c.new.map((line) => ({ side: 'new' as const, line }))];
}

/**
 * Whether a file counts as reviewed: every changed line is covered (or it
 * has none). Read on the base…head comparison, this is "fully reviewed at
 * head", what GitHub's per-file Viewed flag mirrors.
 */
export function fileReviewed(model: ReviewModel, path: string): boolean {
  const f = model.byPath.get(path);
  return !!f && f.status !== 'unknown' && !isUnreviewed(f);
}

/**
 * The writes that mark or unmark every changed line of a file in the
 * comparison: the "Reviewed" toggle of a whole file, and a GitHub Viewed
 * flag imported or cleared. Marked on base…head, that is anchors on the
 * head blob for the added lines and on the base blob for the removed ones,
 * so the file stays covered after a push exactly where its lines did not
 * change. Null while the file's changes are not computed.
 */
export function planFile(diff: ReviewDiff, file: ReviewFile, marks: readonly Anchor[], value: boolean): Plan | null {
  if (!file.changes) return null;
  return planLines(diff, file, marks, file.changes.flatMap(changeLines), value);
}
